import { useState, useRef } from "react";
import { X, ArrowRight, CheckCircle, Loader2, User, MapPin, Shield, MessageCircle, RotateCcw, Download, Phone, Mail, ChevronRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDIAN_STATES = [
  "Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat",
  "Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh",
  "Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab",
  "Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh",
  "Uttarakhand","West Bengal","Andaman & Nicobar Islands","Chandigarh",
  "Dadra & Nagar Haveli and Daman & Diu","Delhi","Jammu & Kashmir","Ladakh",
  "Lakshadweep","Puducherry"
];

function validatePAN(pan) {
  return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pan.toUpperCase());
}

const COUNTRY_CODES = [
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+1", name: "USA", flag: "🇺🇸" },
  { code: "+44", name: "UK", flag: "🇬🇧" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+971", name: "UAE", flag: "🇦🇪" },
  { code: "+65", name: "Singapore", flag: "🇸🇬" },
  { code: "+60", name: "Malaysia", flag: "🇲🇾" },
  { code: "+1", name: "Canada", flag: "🇨🇦" },
];

export default function StartInvestingModal({ open, onClose }) {
  const [step, setStep] = useState("personal");
  const [leadId, setLeadId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState(["", "", "", "", "", ""]);
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [countryCode, setCountryCode] = useState("+91");
  const [consent, setConsent] = useState(true);
  const timerRef = useRef(null);

  const [step1, setStep1] = useState({ name: "", pan: "", email: "", phone: "" });
  const [step2, setStep2] = useState({ address_line1: "", address_line2: "", city: "", state: "", pin_code: "" });

  if (!open) return null;

  const startResendTimer = () => {
    setResendTimer(30);
    timerRef.current = setInterval(() => {
      setResendTimer(t => { if (t <= 1) { clearInterval(timerRef.current); return 0; } return t - 1; });
    }, 1000);
  };

  const validateStep1 = () => {
    const e = {};
    if (!step1.name.trim()) e.name = "Name is required";
    if (!step1.pan.trim()) e.pan = "PAN Number is required";
    else if (!validatePAN(step1.pan)) e.pan = "Invalid PAN format (e.g. ABCDE1234F)";
    if (!step1.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(step1.email)) e.email = "Invalid email address";
    if (!step1.phone.trim()) e.phone = "Mobile number is required";
    else if (countryCode === "+91" && !/^[6-9]\d{9}$/.test(step1.phone.replace(/\s/g, ""))) e.phone = "Enter a valid 10-digit mobile number";
    else if (countryCode !== "+91" && step1.phone.replace(/\s/g, "").length < 6) e.phone = "Enter a valid phone number";
    if (!consent) e.consent = "Please provide consent to proceed";
    return e;
  };

  const validateStep2 = () => {
    const e = {};
    if (!step2.address_line1.trim()) e.address_line1 = "Address is required";
    if (!step2.city.trim()) e.city = "City is required";
    if (!step2.state) e.state = "Please select a state";
    if (!step2.pin_code.trim()) e.pin_code = "PIN code is required";
    else if (!/^\d{6}$/.test(step2.pin_code)) e.pin_code = "Enter a valid 6-digit PIN code";
    return e;
  };

  const submitStep1 = async () => {
    const e = validateStep1();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true);
    try {
      const fullPhone = countryCode === "+91" ? step1.phone.trim() : `${countryCode}${step1.phone.trim()}`;
      const res = await axios.post(`${API}/onboarding/step1`, {
        name: step1.name.trim(),
        pan_number: step1.pan.toUpperCase().trim(),
        email: step1.email.trim().toLowerCase(),
        phone: fullPhone,
      });
      setLeadId(res.data.id);
      if (countryCode === "+91") {
        await sendOTP(res.data.id, step1.phone.trim());
        setStep("otp");
      } else {
        setStep("address");
      }
    } catch (err) {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const sendOTP = async (id, phone) => {
    const lid = id || leadId;
    const ph = phone || step1.phone.trim();
    try {
      await axios.post(`${API}/onboarding/send-otp`, { phone: ph, lead_id: lid });
      setOtpSent(true);
      setOtpCode(["", "", "", "", "", ""]);
      setOtpError("");
      startResendTimer();
    } catch (err) {
      console.error("OTP send failed", err);
    }
  };

  const verifyOTP = async () => {
    const code = otpCode.join("");
    if (code.length < 6) { setOtpError("Please enter the complete 6-digit OTP"); return; }
    setLoading(true);
    setOtpError("");
    try {
      await axios.post(`${API}/onboarding/verify-otp`, { phone: step1.phone.trim(), lead_id: leadId, otp_code: code });
      setStep("address");
    } catch (err) {
      setOtpError(err?.response?.data?.detail || "Incorrect OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const skipOTP = () => setStep("address");

  const submitStep2 = async () => {
    const e = validateStep2();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setLoading(true);
    try {
      await axios.patch(`${API}/onboarding/${leadId}/step2`, {
        address_line1: step2.address_line1.trim(),
        address_line2: step2.address_line2.trim() || null,
        city: step2.city.trim(),
        state: step2.state,
        pin_code: step2.pin_code.trim(),
      });
      setStep("success");
    } catch (err) {
      setErrors({ submit: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep("personal");
    setLeadId(null);
    setErrors({});
    setOtpSent(false);
    setOtpCode(["", "", "", "", "", ""]);
    setOtpError("");
    setStep1({ name: "", pan: "", email: "", phone: "" });
    setStep2({ address_line1: "", address_line2: "", city: "", state: "", pin_code: "" });
    setCountryCode("+91");
    setConsent(true);
    onClose();
  };

  const field = (label, key, obj, setObj, placeholder, type = "text", required = true) => (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--navy)", fontFamily: "DM Sans, sans-serif", marginBottom: 6 }}>
        {label}{required && <span style={{ color: "#dc2626" }}> *</span>}
      </label>
      <input
        type={type}
        placeholder={placeholder}
        value={obj[key]}
        onChange={e => { setObj(o => ({ ...o, [key]: e.target.value })); setErrors(er => ({ ...er, [key]: undefined })); }}
        style={{
          width: "100%", padding: "10px 14px", borderRadius: 10, fontFamily: "DM Sans, sans-serif",
          border: `1.5px solid ${errors[key] ? "#dc2626" : "var(--border)"}`,
          fontSize: 14, outline: "none", color: "var(--text)", background: "white", boxSizing: "border-box",
        }}
      />
      {errors[key] && <p style={{ fontSize: 12, color: "#dc2626", margin: "4px 0 0", fontFamily: "DM Sans, sans-serif" }}>{errors[key]}</p>}
    </div>
  );

  const openFinadoreDeck = () => {
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><title>Finadore — Company Deck</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box}
      body{font-family:'DM Sans',Arial,sans-serif;color:#1e293b;background:linear-gradient(160deg,#eeeef8 0%,#eeeef8 60%,#eaf2ef 100%);print-color-adjust:exact;-webkit-print-color-adjust:exact}
      .page{max-width:800px;margin:0 auto;padding:40px 40px 60px}
      .cover{background:linear-gradient(135deg,#1e1f52 0%,#2b2d6e 50%,#3d3f8f 100%);border-radius:20px;padding:56px 52px;margin-bottom:40px;color:white;position:relative;overflow:hidden}
      .cover::before{content:'';position:absolute;left:-60px;bottom:-60px;width:240px;height:240px;border-radius:50%;background:rgba(16,185,129,0.12)}
      .cover::after{content:'';position:absolute;right:-40px;top:-40px;width:200px;height:200px;border-radius:50%;background:rgba(255,255,255,0.06)}
      .logo{font-size:36px;font-weight:900;letter-spacing:-1px;margin-bottom:6px;display:flex;align-items:center;gap:12px}
      .logo img{width:52px;height:52px;border-radius:50%;border:2px solid rgba(255,255,255,0.3)}
      .tagline{font-size:14px;color:rgba(255,255,255,0.6);margin-bottom:36px}
      .cover h1{font-size:28px;font-weight:800;line-height:1.3;margin-bottom:12px}
      .cover p{font-size:15px;color:rgba(255,255,255,0.8);line-height:1.7;max-width:480px}
      .badges{display:flex;gap:10px;margin-top:28px;flex-wrap:wrap}
      .badge{background:rgba(255,255,255,0.15);border:1px solid rgba(255,255,255,0.25);border-radius:99px;padding:5px 14px;font-size:12px;font-weight:600;color:white}
      .section{margin-bottom:40px}
      .section-label{font-size:11px;font-weight:700;color:#4a7c6f;text-transform:uppercase;letter-spacing:1px;margin-bottom:8px}
      .section-title{font-size:22px;font-weight:800;color:#2b2d6e;margin-bottom:20px;font-family:'DM Serif Display',Arial,sans-serif}
      .grid-2{display:grid;grid-template-columns:1fr 1fr;gap:16px}
      .grid-3{display:grid;grid-template-columns:1fr 1fr 1fr;gap:16px}
      .pointer-card{background:white;border-radius:14px;padding:20px;border:1px solid #e2e8f0;display:flex;gap:14px;align-items:flex-start;page-break-inside:avoid;break-inside:avoid}
      .pointer-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:22px}
      .pointer-title{font-size:14px;font-weight:700;color:#1e293b;margin:0 0 5px;font-family:'DM Serif Display',Arial,sans-serif}
      .pointer-text{font-size:12px;color:#64748b;line-height:1.65;margin:0}
      .card{background:white;border:1px solid #e2e8f0;border-radius:14px;padding:20px;page-break-inside:avoid;break-inside:avoid}
      .card h3{font-size:15px;font-weight:700;color:#2b2d6e;margin-bottom:6px;font-family:'DM Serif Display',Arial,sans-serif}
      .card p{font-size:13px;color:#64748b;line-height:1.6}
      .card .icon{width:38px;height:38px;border-radius:10px;display:flex;align-items:center;justify-content:center;margin-bottom:12px;font-size:18px}
      .stat-card{background:linear-gradient(135deg,#2b2d6e,#3d3f8f);border-radius:14px;padding:20px;color:white;text-align:center;page-break-inside:avoid;break-inside:avoid}
      .stat-card .num{font-size:28px;font-weight:900;color:#a7d7c9;margin-bottom:4px}
      .stat-card .lbl{font-size:12px;color:rgba(255,255,255,0.7)}
      .highlight-box{background:#eeeef8;border-left:4px solid #2b2d6e;border-radius:0 12px 12px 0;padding:16px 20px;margin-bottom:16px}
      .highlight-box h4{font-size:14px;font-weight:700;color:#2b2d6e;margin-bottom:4px}
      .highlight-box p{font-size:13px;color:#475569;line-height:1.6}
      .green-box{background:#eaf2ef;border-left:4px solid #4a7c6f;border-radius:0 12px 12px 0;padding:16px 20px}
      .green-box h4{font-size:14px;font-weight:700;color:#4a7c6f;margin-bottom:4px}
      .green-box p{font-size:13px;color:#2a5c52;line-height:1.6}
      .divider{border:none;border-top:1px solid #e2e8f0;margin:32px 0}
      .footer{background:#2b2d6e;border-radius:14px;padding:28px 36px;color:white;display:flex;justify-content:space-between;align-items:center}
      .footer-left h3{font-size:20px;font-weight:900;margin-bottom:4px}
      .footer-left p{font-size:12px;color:rgba(255,255,255,0.6)}
      .footer-right{text-align:right;font-size:12px;color:rgba(255,255,255,0.7);line-height:1.8}
      .pill{display:inline-block;background:#4a7c6f;color:white;font-size:10px;font-weight:700;padding:2px 10px;border-radius:99px;margin-left:8px;vertical-align:middle}
      @media print{body{background:white}.page{padding:20px}}
    </style></head>
    <body><div class="page">

      <!-- Cover -->
      <div class="cover">
        <div class="logo"><img src="https://customer-assets.emergentagent.com/job_invest-calculate/artifacts/stcncr10_Logo%20-%20small.jpg" alt="Finadore Logo">Finadore</div>
        <div class="tagline">We Adore Your Investments</div>
        <h1>Your Trusted Partner for<br>Wealth Creation in India</h1>
        <p>Empowering professionals to build lasting wealth through Mutual Funds, SIP, PMS, Specialised Investment Funds, and the exclusive StethoWealth programme for doctors.</p>
        <div class="badges">
          <span class="badge">AMFI Registered ARN-300809</span>
          <span class="badge">25+ Years of Experience</span>
          <span class="badge">AUM ₹150 Cr+</span>
        </div>
      </div>

      <!-- Stats -->
      <div class="section">
        <div class="section-label">By the Numbers</div>
        <div class="section-title">Finadore at a Glance</div>
        <div class="grid-3">
          <div class="stat-card"><div class="num">₹150 Cr+</div><div class="lbl">Assets Under Management</div></div>
          <div class="stat-card"><div class="num">25+</div><div class="lbl">Years of Combined Experience</div></div>
          <div class="stat-card"><div class="num">500+</div><div class="lbl">Investors Served</div></div>
        </div>
      </div>

      <hr class="divider">

      <!-- Services -->
      <div class="section">
        <div class="section-label">What We Offer</div>
        <div class="section-title">Our Financial Services</div>
        <div class="grid-2">
          <div class="card">
            <div class="icon" style="background:#eeeef8">📈</div>
            <h3>Mutual Funds (SIP & Lumpsum)</h3>
            <p>Curated, goal-aligned mutual fund portfolios across Large Cap, Mid Cap, Small Cap, ELSS, and Hybrid categories.</p>
          </div>
          <div class="card">
            <div class="icon" style="background:#eaf2ef">🏆</div>
            <h3>PMS — Portfolio Management</h3>
            <p>Professional Portfolio Management Services for HNIs and UHNIs seeking alpha over index with personalised strategies.</p>
          </div>
          <div class="card">
            <div class="icon" style="background:#fbf0e4">💼</div>
            <h3>Specialised Investment Fund (SIF)</h3>
            <p>Alternative investment strategies beyond traditional mutual funds, designed for sophisticated investors seeking higher returns.</p>
          </div>
          <div class="card">
            <div class="icon" style="background:#eeeef8">🏦</div>
            <h3>FDs, Bonds & Fixed Income</h3>
            <p>54EC Capital Gains Bonds, RBI Floating Rate Bonds, Mahindra Finance FDs, and AAA-rated Corporate FDs.</p>
          </div>
          <div class="card">
            <div class="icon" style="background:#FEE2E2">🩺</div>
            <h3>StethoWealth — For Doctors</h3>
            <p>India's only wealth management programme exclusively designed for medical professionals — because doctors deserve specialist wealth planning too.</p>
          </div>
          <div class="card">
            <div class="icon" style="background:#eaf2ef">📊</div>
            <h3>Tax Planning & Insurance</h3>
            <p>Comprehensive tax planning through ELSS, NPS, and life/health insurance solutions aligned with your income bracket.</p>
          </div>
        </div>
      </div>

      <hr class="divider">

      <!-- Why Finadore — pointer style -->
      <div class="section">
        <div class="section-label">Why Choose Us</div>
        <div class="section-title">The Finadore Difference</div>
        <div style="display:flex;flex-direction:column;gap:12px">
          <div class="pointer-card">
            <div class="pointer-icon" style="background:#eeeef8">🎯</div>
            <div><p class="pointer-title">Goal-Based, Not Product-Based</p><p class="pointer-text">We start with your life goals — retirement, children's education, wealth creation — and then select the right instruments. We never push products; we build plans.</p></div>
          </div>
          <div class="pointer-card">
            <div class="pointer-icon" style="background:#eaf2ef">🔒</div>
            <div><p class="pointer-title">SEBI &amp; AMFI Compliant</p><p class="pointer-text">Finadore is an AMFI Registered Mutual Fund Distributor (ARN-300809). Your investments are always held with the AMC — fully regulated and safe.</p></div>
          </div>
          <div class="pointer-card">
            <div class="pointer-icon" style="background:#fbf0e4">📊</div>
            <div><p class="pointer-title">25+ Years of Combined Experience</p><p class="pointer-text">Our team brings deep expertise across mutual funds, PMS, SIF, fixed income, and tax planning — guiding investors through multiple market cycles.</p></div>
          </div>
          <div class="pointer-card">
            <div class="pointer-icon" style="background:#eeeef8">⚡</div>
            <div><p class="pointer-title">Paperless, Quick Onboarding</p><p class="pointer-text">Start your investment journey in under 10 minutes with 100% digital, paperless KYC. No branch visits, no paperwork delays.</p></div>
          </div>
        </div>
      </div>

      <hr class="divider">

      <!-- Footer -->
      <div class="footer">
        <div class="footer-left">
          <h3>Finadore</h3>
          <p>We Adore Your Investments</p>
        </div>
        <div class="footer-right">
          <div>hello@finadore.com</div>
          <div>+91 62803 07033</div>
          <div>AMFI Registered · ARN-300809</div>
          <div style="margin-top:6px;font-size:10px">Regus PPR Mall, Jalandhar · WeWork, Gurgaon</div>
        </div>
      </div>

      <p style="text-align:center;font-size:10px;color:#94a3b8;margin-top:16px">Mutual fund investments are subject to market risks. Read all scheme-related documents carefully. Past performance is not indicative of future results.</p>

    </div>
    <script>window.onload=function(){window.print()}</script>
    </body></html>`);
    w.document.close();
  };

  const headerTitle = step === "personal" ? "Personal Details" : step === "otp" ? "Verify WhatsApp" : step === "address" ? "Address Details" : "You're All Set!";

  return (
    <div data-testid="start-investing-modal" style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: "rgba(10,20,60,0.6)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} onClick={e => { if (e.target === e.currentTarget) handleClose(); }}>
      <div style={{
        background: "white", borderRadius: 24, width: "100%", maxWidth: 500,
        boxShadow: "0 32px 80px rgba(0,0,0,0.22)", overflow: "hidden",
        position: "relative", maxHeight: "94vh", overflowY: "auto",
      }}>
        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #2b2d6e 0%, var(--navy-light) 100%)", padding: "24px 28px 20px", position: "relative" }}>
          <button onClick={handleClose} style={{ position: "absolute", top: 14, right: 14, background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "white" }}>
            <X size={15} />
          </button>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", fontFamily: "DM Sans, sans-serif", margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.5px" }}>Start Your Investment Journey</p>
          <h2 style={{ color: "white", fontFamily: "DM Serif Display, serif", fontSize: 20, fontWeight: 800, margin: 0 }}>{headerTitle}</h2>

          {step !== "success" && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 16 }}>
              {[{ id: "personal", label: "Personal" }, { id: "address", label: "Address" }].map((s, i) => {
                const activeIdx = step === "otp" ? 0 : step === "personal" ? 0 : step === "address" ? 1 : 2;
                return (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                      background: activeIdx > i ? "var(--green)" : activeIdx === i ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)",
                      fontSize: 11, fontWeight: 700, color: "white", fontFamily: "DM Sans, sans-serif",
                      border: activeIdx === i ? "2px solid white" : "none",
                    }}>
                      {activeIdx > i ? <CheckCircle size={13} /> : i + 1}
                    </div>
                    <span style={{ fontSize: 11, color: activeIdx >= i ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.4)", fontFamily: "DM Sans, sans-serif" }}>{s.label}</span>
                    {i < 1 && <div style={{ width: 20, height: 1, background: activeIdx > i ? "var(--green)" : "rgba(255,255,255,0.2)", margin: "0 2px" }} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Body */}
        <div style={{ padding: "24px 28px 28px" }}>

          {/* STEP: Personal Details */}
          {step === "personal" && (
            <div data-testid="onboarding-step1">
              <div style={{ display: "flex", gap: 8, alignItems: "center", background: "#eeeef8", borderRadius: 10, padding: "9px 13px", marginBottom: 20 }}>
                <User size={14} color="var(--navy)" />
                <span style={{ fontSize: 12, fontFamily: "DM Sans, sans-serif", color: "var(--navy)" }}>Enter details exactly as on your PAN card</span>
              </div>
              {field("Full Name (as per PAN Card)", "name", step1, setStep1, "e.g. RAJESH KUMAR MEHTA")}
              {field("PAN Number", "pan", step1, setStep1, "e.g. ABCDE1234F")}
              {field("Email ID", "email", step1, setStep1, "your@email.com", "email")}

              {/* Phone with country code */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--navy)", fontFamily: "DM Sans, sans-serif", marginBottom: 6 }}>
                  Mobile Number <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <select value={countryCode} onChange={e => setCountryCode(e.target.value)}
                    style={{ flexShrink: 0, padding: "10px 10px", borderRadius: 10, border: "1.5px solid var(--border)", fontSize: 13, fontFamily: "DM Sans, sans-serif", outline: "none", color: "var(--text)", background: "white", cursor: "pointer" }}>
                    {COUNTRY_CODES.map((c, i) => (
                      <option key={i} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                  <input type="tel" placeholder="9876543210" value={step1.phone}
                    onChange={e => { setStep1(o => ({ ...o, phone: e.target.value })); setErrors(er => ({ ...er, phone: undefined })); }}
                    style={{ flex: 1, padding: "10px 14px", borderRadius: 10, fontFamily: "DM Sans, sans-serif", border: `1.5px solid ${errors.phone ? "#dc2626" : "var(--border)"}`, fontSize: 14, outline: "none", color: "var(--text)", background: "white" }}
                  />
                </div>
                {errors.phone && <p style={{ fontSize: 12, color: "#dc2626", margin: "4px 0 0", fontFamily: "DM Sans, sans-serif" }}>{errors.phone}</p>}
                {countryCode !== "+91" && (
                  <p style={{ fontSize: 11, color: "#c4833a", margin: "4px 0 0", fontFamily: "DM Sans, sans-serif" }}>
                    WhatsApp OTP verification is only available for Indian (+91) numbers. Address step will be next.
                  </p>
                )}
              </div>

              {/* Consent */}
              <div style={{ background: "#eeeef8", border: "1.5px solid var(--border)", borderRadius: 10, padding: "10px 14px", marginBottom: 14 }}>
                <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
                  <input type="checkbox" checked={consent} onChange={e => { setConsent(e.target.checked); setErrors(er => ({ ...er, consent: undefined })); }}
                    style={{ marginTop: 2, flexShrink: 0, width: 15, height: 15, accentColor: "var(--navy)", cursor: "pointer" }} />
                  <span style={{ fontSize: 12, color: "var(--text)", fontFamily: "DM Sans, sans-serif", lineHeight: 1.6 }}>
                    I consent to be contacted by Finadore representatives via phone, WhatsApp, or email for investment services and products offered by Finadore. <span style={{ color: "#dc2626", fontWeight: 600 }}>*</span>
                  </span>
                </label>
                {errors.consent && <p style={{ fontSize: 12, color: "#dc2626", margin: "6px 0 0", fontFamily: "DM Sans, sans-serif" }}>{errors.consent}</p>}
              </div>
              {errors.submit && <p style={{ color: "#dc2626", fontSize: 12, marginBottom: 10, fontFamily: "DM Sans, sans-serif" }}>{errors.submit}</p>}
              <button onClick={submitStep1} disabled={loading}
                style={{ width: "100%", padding: "12px", background: "var(--navy)", color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 4, opacity: loading ? 0.8 : 1 }}
                data-testid="onboarding-step1-submit">
                {loading ? <Loader2 size={17} style={{ animation: "spin 1s linear infinite" }} /> : <>Continue <ArrowRight size={16} /></>}
              </button>
              <p style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 12 }}>
                <span style={{ display: "inline-flex", width: 14, height: 14, borderRadius: "50%", background: "#eaf2ef", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Shield size={9} color="#4a7c6f" />
                </span>
                Your information is encrypted and secure. We never share your data.
              </p>
            </div>
          )}

          {/* STEP: WhatsApp OTP */}
          {step === "otp" && (
            <div data-testid="onboarding-otp">
              <div style={{ textAlign: "center", marginBottom: 20 }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#eaf2ef", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
                  <MessageCircle size={26} color="#4a7c6f" />
                </div>
                <p style={{ fontSize: 14, color: "var(--text)", fontFamily: "DM Sans, sans-serif", lineHeight: 1.6, margin: 0 }}>
                  We've sent a 6-digit OTP to your WhatsApp<br />
                  <strong style={{ color: "var(--navy)" }}>{countryCode} {step1.phone}</strong>
                </p>
              </div>

              {/* OTP Input boxes */}
              <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
                {otpCode.map((digit, i) => (
                  <input
                    key={i}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    data-testid={`otp-box-${i}`}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, "");
                      const newCode = [...otpCode];
                      newCode[i] = val;
                      setOtpCode(newCode);
                      setOtpError("");
                      if (val && i < 5) document.querySelector(`[data-testid="otp-box-${i + 1}"]`)?.focus();
                    }}
                    onKeyDown={e => {
                      if (e.key === "Backspace" && !otpCode[i] && i > 0) {
                        document.querySelector(`[data-testid="otp-box-${i - 1}"]`)?.focus();
                      }
                    }}
                    style={{
                      width: 46, height: 52, textAlign: "center", fontSize: 20, fontWeight: 700,
                      borderRadius: 10, border: `2px solid ${otpError ? "#dc2626" : digit ? "var(--navy)" : "var(--border)"}`,
                      fontFamily: "DM Sans, sans-serif", outline: "none", color: "var(--navy)",
                    }}
                  />
                ))}
              </div>

              {otpError && <p style={{ fontSize: 12, color: "#dc2626", textAlign: "center", marginBottom: 12, fontFamily: "DM Sans, sans-serif" }}>{otpError}</p>}

              <button onClick={verifyOTP} disabled={loading}
                style={{ width: "100%", padding: "12px", background: "var(--navy)", color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.8 : 1 }}
                data-testid="otp-verify-btn">
                {loading ? <Loader2 size={17} style={{ animation: "spin 1s linear infinite" }} /> : <>Verify OTP <CheckCircle size={16} /></>}
              </button>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 14 }}>
                <button onClick={() => sendOTP()} disabled={resendTimer > 0}
                  style={{ background: "none", border: "none", fontSize: 12, color: resendTimer > 0 ? "#94a3b8" : "var(--navy)", fontFamily: "DM Sans, sans-serif", cursor: resendTimer > 0 ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 4, padding: 0, fontWeight: 600 }}>
                  <RotateCcw size={12} /> {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
                </button>
                <button onClick={skipOTP}
                  style={{ background: "none", border: "none", fontSize: 12, color: "#64748b", fontFamily: "DM Sans, sans-serif", cursor: "pointer", padding: 0, textDecoration: "underline" }}
                  data-testid="otp-skip-btn">
                  Skip for now
                </button>
              </div>

              <p style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "DM Sans, sans-serif", textAlign: "center", marginTop: 14, lineHeight: 1.5 }}>
                OTP valid for 10 minutes.
              </p>
            </div>
          )}

          {/* STEP: Address */}
          {step === "address" && (
            <div data-testid="onboarding-step2">
              <div style={{ display: "flex", gap: 8, alignItems: "center", background: "#eaf2ef", borderRadius: 10, padding: "9px 13px", marginBottom: 20 }}>
                <MapPin size={14} color="#4a7c6f" />
                <span style={{ fontSize: 12, fontFamily: "DM Sans, sans-serif", color: "#4a7c6f" }}>We need your address for KYC purposes</span>
              </div>
              {field("Address Line 1", "address_line1", step2, setStep2, "House/Flat No., Street, Locality")}
              {field("Address Line 2", "address_line2", step2, setStep2, "Landmark (optional)", "text", false)}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                {field("City", "city", step2, setStep2, "New Delhi")}
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--navy)", fontFamily: "DM Sans, sans-serif", marginBottom: 6 }}>
                    State <span style={{ color: "#dc2626" }}>*</span>
                  </label>
                  <select value={step2.state}
                    onChange={e => { setStep2(s => ({ ...s, state: e.target.value })); setErrors(er => ({ ...er, state: undefined })); }}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 10, border: `1.5px solid ${errors.state ? "#dc2626" : "var(--border)"}`, fontSize: 14, fontFamily: "DM Sans, sans-serif", outline: "none", color: step2.state ? "var(--text)" : "#94a3b8", background: "white", boxSizing: "border-box" }}>
                    <option value="">Select State</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.state && <p style={{ fontSize: 12, color: "#dc2626", margin: "4px 0 0", fontFamily: "DM Sans, sans-serif" }}>{errors.state}</p>}
                </div>
              </div>
              {field("PIN Code", "pin_code", step2, setStep2, "110001")}
              {errors.submit && <p style={{ color: "#dc2626", fontSize: 12, marginBottom: 10, fontFamily: "DM Sans, sans-serif" }}>{errors.submit}</p>}
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={() => setStep(countryCode === "+91" ? "otp" : "personal")}
                  style={{ flex: "0 0 auto", padding: "12px 18px", background: "var(--surface)", color: "var(--navy)", border: "1.5px solid var(--border)", borderRadius: 12, fontSize: 13, fontWeight: 600, fontFamily: "DM Sans, sans-serif", cursor: "pointer" }}>
                  Back
                </button>
                <button onClick={submitStep2} disabled={loading}
                  style={{ flex: 1, padding: "12px", background: "var(--navy)", color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, opacity: loading ? 0.8 : 1 }}
                  data-testid="onboarding-step2-submit">
                  {loading ? <Loader2 size={17} style={{ animation: "spin 1s linear infinite" }} /> : <>Submit Details <ArrowRight size={16} /></>}
                </button>
              </div>
              <p style={{ fontSize: 11, color: "var(--text-muted)", fontFamily: "DM Sans, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 5, marginTop: 12 }}>
                <span style={{ display: "inline-flex", width: 14, height: 14, borderRadius: "50%", background: "#eaf2ef", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Shield size={9} color="#4a7c6f" />
                </span>
                Your information is encrypted and secure.
              </p>
            </div>
          )}

          {/* STEP: Success */}
          {step === "success" && (
            <div data-testid="onboarding-step3" style={{ padding: "4px 0" }}>
              {/* Success hero */}
              <div style={{ textAlign: "center", marginBottom: 24 }}>
                <div style={{ position: "relative", display: "inline-block", marginBottom: 14 }}>
                  <div style={{ width: 76, height: 76, borderRadius: "50%", background: "linear-gradient(135deg,#eaf2ef,#a7d7c9)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 0 8px #eaf2ef" }}>
                    <CheckCircle size={38} color="#4a7c6f" strokeWidth={2.5} />
                  </div>
                </div>
                <h3 style={{ color: "var(--navy)", fontFamily: "DM Serif Display, serif", fontSize: 22, fontWeight: 800, margin: "0 0 8px" }}>
                  Welcome, {step1.name.split(" ")[0]}!
                </h3>
                <p style={{ color: "var(--text-muted)", fontFamily: "DM Sans, sans-serif", fontSize: 14, lineHeight: 1.7, margin: "0 auto", maxWidth: 340 }}>
                  Your details are saved. Our <strong style={{ color: "var(--navy)" }}>executive</strong> will be in touch with you shortly.
                </p>
              </div>

              {/* What happens next — progress stepper */}
              <div style={{ marginBottom: 18 }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: "var(--navy)", textTransform: "uppercase", letterSpacing: "0.5px", margin: "0 0 14px", fontFamily: "DM Sans, sans-serif" }}>What happens next</p>
                <div style={{ position: "relative" }}>
                  {/* vertical line */}
                  <div style={{ position: "absolute", left: 11, top: 22, bottom: 22, width: 2, background: "linear-gradient(to bottom, var(--green), #C7D2FE)", zIndex: 0 }} />
                  {[
                    { label: "Our Executive will call you", sub: "Within 24 working hours", done: false },
                    { label: "Will note down your requirements", sub: "Goals, risk appetite, time horizon", done: false },
                    { label: "Funds recommended & investment started", sub: "We handle the paperwork for you", done: false },
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: i < 2 ? 16 : 0, position: "relative", zIndex: 1 }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: item.done ? "var(--green)" : "#eeeef8", border: `2px solid ${item.done ? "var(--green)" : "#C7D2FE"}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {item.done
                          ? <CheckCircle size={13} color="white" strokeWidth={3} />
                          : <span style={{ fontSize: 11, fontWeight: 700, color: "var(--navy)", fontFamily: "DM Sans" }}>{i + 1}</span>
                        }
                      </div>
                      <div>
                        <p style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)", fontFamily: "DM Sans, sans-serif", margin: "2px 0 2px" }}>{item.label}</p>
                        <p style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "DM Sans, sans-serif", margin: 0 }}>{item.sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Download deck */}
              <button onClick={openFinadoreDeck}
                style={{ width: "100%", padding: "11px 16px", background: "var(--navy)", color: "white", border: "none", borderRadius: 12, fontSize: 13, fontWeight: 700, fontFamily: "DM Sans, sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}
                data-testid="download-deck-btn">
                <Download size={15} /> Know more about Finadore
              </button>

              <a href="https://calendly.com/ankush-finadore/30min" target="_blank" rel="noopener noreferrer"
                style={{ width: "100%", padding: "11px 16px", background: "#eeeef8", color: "var(--navy)", border: "1.5px solid #C7D2FE", borderRadius: 12, fontSize: 13, fontWeight: 700, fontFamily: "DM Sans, sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10, textDecoration: "none" }}
                data-testid="book-call-btn">
                <Phone size={14} /> Invest 30 Mins, Build Long-Term Wealth
              </a>

              {/* Contact strip */}
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <a href="mailto:hello@finadore.com" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#eeeef8", border: "1px solid var(--border)", borderRadius: 10, padding: "9px 10px", textDecoration: "none" }}>
                  <Mail size={13} color="var(--navy)" strokeWidth={2} />
                  <span style={{ fontSize: 11, fontFamily: "DM Sans, sans-serif", color: "var(--navy)", fontWeight: 600, whiteSpace: "nowrap" }}>hello@finadore.com</span>
                </a>
                <a href="tel:+916280307033" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, background: "#eeeef8", border: "1px solid var(--border)", borderRadius: 10, padding: "9px 10px", textDecoration: "none" }}>
                  <Phone size={13} color="var(--navy)" strokeWidth={2} />
                  <span style={{ fontSize: 11, fontFamily: "DM Sans, sans-serif", color: "var(--navy)", fontWeight: 600, whiteSpace: "nowrap" }}>+91 62803 07033</span>
                </a>
              </div>

              <button onClick={handleClose}
                style={{ width: "100%", padding: "12px", background: "var(--green)", color: "white", border: "none", borderRadius: 12, fontSize: 14, fontWeight: 700, fontFamily: "DM Sans, sans-serif", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                data-testid="onboarding-done-btn">
                Done <ChevronRight size={16} />
              </button>

              <p style={{ textAlign: "center", fontSize: 11, color: "var(--text-muted)", fontFamily: "DM Sans, sans-serif", margin: "12px 0 0" }}>
                AMFI Registered Mutual Fund Distributor · ARN-300809
              </p>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
